import React, { useState, useEffect } from "react";
import CompanyHeader from "../../components/CompanyHeader";
import CompanySideMenu from "../../components/CompanySideMenu";
import { useNavigate, useParams } from "react-router-dom";
import CompanyInformationTab from "./components/CompanyInformationTab";
import DepartmentMessageTab from "./components/DepartmentMessageTab";
import JobInformationTab from "./components/JobInformationTab";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { getJobDetail, updateJobDetail, setJobDetailData, setSuccess, setError, setJobDetailDataTemp, setIsChangeBeforePreview, setIsPublishedItem } from "../../reducers/companyJob";
import { formatDate, parseTags, renderContentWithTrackedLinks } from "../../utils/format";
import { PERMISSIONS } from '../../constants/index';
import ReturnButton from "../../components/ReturnButton";
import CustomButton from "../../components/CustomButton";
import NotFoundPage from "../../pages/NotFoundPage";
import Copyright from "../../components/Copyright";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import { getMinPublicTime } from "../../utils/util";
import i18n from 'i18next';
import { setIsFormDirty, setShowLeaveConfirm } from "../../reducers/app";
import UnsaveConfirmPopup from "../action/components/UnsaveConfirmPopup";

const JobPreview = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const currentUser = useSelector((state: any) => state.currentUser);
    const { showLeaveConfirm, isNavigatingAway } = useSelector((state: any) => state.app);
    const { staffPermission } = currentUser;
    const {
        actionName,
        actionStatus,
        jobDetailData,
        jobDetailDataTemp,
        success, error, isChangeBeforePreview, isPublishedItem,
    } = useSelector((state: any) => state.companyJob);
    const { id } = useParams<{ id: string }>();
    const [jobDetail, setJobDetail] = useState<any>([]);

    useEffect(() => {
        dispatch(setIsPublishedItem(false));
        if(isChangeBeforePreview) {
            dispatch(setIsFormDirty(true));
            dispatch(setShowLeaveConfirm(false));
        }
    }, [])
    
    useEffect(() => {
        if(id && !jobDetailDataTemp){
            const data = {
                id: Number(id),
            }
            dispatch(getJobDetail(data));
        }
        
        if(jobDetailDataTemp){
            const data = {
                ...jobDetailDataTemp,
                occupation_1: jobDetailDataTemp.jobType,
                occupation_2: jobDetailDataTemp.jobType2,
                occupation_3: jobDetailDataTemp.jobType3,
                industry_1: jobDetailDataTemp.industry1,
                industry_2: jobDetailDataTemp.industry2,
                industry_3: jobDetailDataTemp.industry3,
                location_city: {
                    id:	jobDetailDataTemp.prefecture.id,
                    label:	jobDetailDataTemp.prefecture.name
                },
                location_village: jobDetailDataTemp.municipalities,
                location_building: jobDetailDataTemp.street_address,
                location_station: jobDetailDataTemp.nearest_station,
                annual_salary_min: jobDetailDataTemp.from_income,
                annual_salary_max: jobDetailDataTemp.to_income,
                description: jobDetailDataTemp.job_description,
                is_change_scope: jobDetailDataTemp.scope_of_change,
                change_scope: jobDetailDataTemp.scope_of_change_note,
                message_from_department: jobDetailDataTemp.department_message,
                role: {
                    id:	jobDetailDataTemp.post.id,
                    label:	jobDetailDataTemp.post.name
                },
                persons_to_hire: jobDetailDataTemp.employee_quantity,
                location_note: jobDetailDataTemp.work_location_notes,
                void_smoking_policy: jobDetailDataTemp.smoking_measure,
                monthly_salary_min: jobDetailDataTemp.from_monthly_fee,
                monthly_salary_max: jobDetailDataTemp.to_monthly_fee,
                bonuses_per_year: jobDetailDataTemp.bonus,
                salary_payment_method: {
                    id: jobDetailDataTemp.pay_type.id,
                    label: jobDetailDataTemp.pay_type.name,
                },
                has_salary_increment: jobDetailDataTemp.salary_increase ? "True" : "False",
                insurance_note: jobDetailDataTemp.insurance_benefit_note,
                bonus_note: jobDetailDataTemp.allowance_note,
                type: {
                    id:	jobDetailDataTemp.employment_status?.id,
                    label:	jobDetailDataTemp.employment_status?.name
                },
                working_style: {
                    id:	jobDetailDataTemp.working_style.id,
                    label:	jobDetailDataTemp.working_style.name
                },
                working_hour_from: jobDetailDataTemp.from_working_hour + ":" + jobDetailDataTemp.from_working_minute + ":00",
                working_hour_to: jobDetailDataTemp.to_working_hour + ":" + jobDetailDataTemp.to_working_minute + ":00",
                monthly_average_ot_hours: jobDetailDataTemp.avg_ot_hours,
                fixed_OT_payment: jobDetailDataTemp.fixed_ot_pay ? "True" : "False",
                fixed_OT_payment_note: jobDetailDataTemp.ot_pay_note,
                working_off_days_note: jobDetailDataTemp.vacation_note,
                days_off_pattern: jobDetailDataTemp.holiday_vacation,
                annual_working_off_day: jobDetailDataTemp.annual_holiday,
                probation_duration: jobDetailDataTemp.trial_period ? "True" : "False",
                probation_months: jobDetailDataTemp.trial_period_time,
                wage_note: jobDetailDataTemp.wage_remarks,
                type_note: jobDetailDataTemp.employment_status_note,
            }
            dispatch(setJobDetailData(data));
        }
    }, [id]);

    const getPublishedDateDisplay = (item) => {
        let jobDate = formatDate(new Date());
        if(item.is_public_scheduled && item.public_schedule_date) {
            jobDate = formatDate(item.public_schedule_date);
        }
        else if(item.public_date) {
            jobDate = formatDate(item.public_date);
        }
        
        return "公開日　" + jobDate + (item.expiry_date ? "　〜　募集終了日　" + formatDate(item.expiry_date) : "")
    }

    useEffect(() => {
        const jobDetail = {
            ...jobDetailData,
            occupation: jobDetailData.occupation_2?.name,
            location: [
                jobDetailData.location_city?.label,
                jobDetailData.location_village,
                jobDetailData.location_building
            ].filter(Boolean).join(" "),
            salary: "年収 " + `${jobDetailData.annual_salary_min ? `${new Intl.NumberFormat().format(jobDetailData.annual_salary_min)} 万円` : 'ー'}` + " 〜 " + `${jobDetailData.annual_salary_max ? `${new Intl.NumberFormat().format(jobDetailData.annual_salary_max)} 万円` : 'ー'}`,
            jobDate: getPublishedDateDisplay(jobDetailData),
            tag: jobDetailData.tag ? parseTags(String(jobDetailData.tag)) : "",
        }
        setJobDetail(jobDetail);
        
         //redirect to detail after update
        if(isPublishedItem) {
            navigate("/company/job/detail/" + jobDetailData.id);
        }
    }, [jobDetailData, jobDetailDataTemp]);

    const validateScheduleDate = (scheduleDate: any): { hasError: boolean; errorMsg: string } => {
        let hasError = false;
        let errorMsg = null;

        if (scheduleDate) {
            const scheduled = new Date(scheduleDate);
            const nowPlus1Hour = getMinPublicTime();

            if (scheduled < nowPlus1Hour) {
                hasError = true;
                errorMsg = i18n.t('MSG_GLOBAL_PUBLISH_SCHEDULE_MIN_ONE_HOUR');
            }
        }

        return { hasError, errorMsg };
    };

    const handleSetIsPublic = () => {
        if(jobDetailData){
            dispatch(setIsFormDirty(false));
            dispatch(setShowLeaveConfirm(false));
            dispatch(setIsChangeBeforePreview(false));

            if(jobDetailData.is_public_scheduled && jobDetailData.public_schedule_date) {
                const { hasError, errorMsg } = validateScheduleDate(jobDetailData.public_schedule_date);
                if (hasError && errorMsg) {
                    showCustomToast({title: "入力内容を修正してください", message: errorMsg, variant: "error", type: "display_title"});
                    return;
                }
            }
            const isInitialPublish = !jobDetailDataTemp && jobDetailData.is_draft;
            const data = {
                id: jobDetailData.id,
                requestBody: {
                    ...(isInitialPublish
                        ? {
                            is_public_scheduled: jobDetailData.is_public_scheduled,
                            public_schedule_date: jobDetailData.public_schedule_date,
                        } 
                        : { ...jobDetailData }
                    ),
                    is_public: true,
                },
            }
            
            dispatch(updateJobDetail(data));
            dispatch(setIsPublishedItem(true));
            dispatch(setJobDetailDataTemp(null));
        }
    }

    const handleGoToEdit = () => {
        dispatch(setShowLeaveConfirm(false));

        if(jobDetail.id) navigate("/company/job/edit/" + jobDetail.id);
        return;
    }

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const showCustomToast = ({title="メッセージ", message, variant="success", type="normal"}) => {
        enqueueSnackbar("I love snacks.", {
            content: (key) => (
                <CustomToast
                    title={title}
                    message={message}
                    variant={variant}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                    type={type}
                />
            ),
        });
    }
    useEffect(() => {
        if(error) {
            showCustomToast({message: error, variant: "error"});
            dispatch(setError(null));
        }
        else if(success) {
            showCustomToast({message: success});
            dispatch(setSuccess(null));
        }
    }, [error, success]);

    if(id && jobDetailData.length === 0) {
        if(actionName == "getJobDetail" && actionStatus == "rejected") return <NotFoundPage />;
        return null;
    }

    const handleBack = () => {
        if(jobDetailData?.is_draft) {
            dispatch(setShowLeaveConfirm(true));
        } else {
            dispatch(setShowLeaveConfirm(false));
            navigate(-1);
        }
    }

    return (
        <>
        <CompanyHeader />
        <div className="mt-6 px-6 flex gap-6 relative">
            <CompanySideMenu />
            <div className="flex-1">
            <div className="mb-4 flex items-center gap-4">
                <ReturnButton onClick={() => handleBack()} />
                <span className="text-sm alu-text-base-1">/</span>
                <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                    {jobDetail.title} のプレビュー
                </h3>
            </div>

            <div className="flex gap-6">
                <div className="w-full pb-6 pt-8 rounded-2xl bg-white h-max alu-section-box-shadow mb-6">
                <div className="px-6 mb-8">
                    <h2 className="mb-4 text-2xl font-bold alu-text-base-2">
                    {jobDetail.title}
                    </h2>
                    {jobDetail.tag && (
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                    {jobDetail?.tag.map((tag: any, idx: any) => (
                        <span
                        key={idx}
                        className="px-2 py-[0.125rem] rounded border bg-white border-[#808080] text-xs font-bold text-center"
                        >
                        {tag}
                        </span>
                    ))}
                    </div>
                    )}
                    <div className="flex flex-col gap-2 mb-4">
                    <div className="flex items-center gap-[1.125rem]">
                        <p className="min-w-[80px] text-base alu-text-base-2 flex items-center">
                        <span className="mr-1">
                            <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            >
                            <mask
                                id="mask0_13672_2581"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="24"
                                height="24"
                            >
                                <rect width="24" height="24" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_13672_2581)">
                                <path
                                d="M1 20V17.2C1 16.6333 1.14583 16.1125 1.4375 15.6375C1.72917 15.1625 2.11667 14.8 2.6 14.55C3.63333 14.0333 4.68333 13.6458 5.75 13.3875C6.81667 13.1292 7.9 13 9 13C10.1 13 11.1833 13.1292 12.25 13.3875C13.3167 13.6458 14.3667 14.0333 15.4 14.55C15.8833 14.8 16.2708 15.1625 16.5625 15.6375C16.8542 16.1125 17 16.6333 17 17.2V20H1ZM19 20V17C19 16.2667 18.7958 15.5625 18.3875 14.8875C17.9792 14.2125 17.4 13.6333 16.65 13.15C17.5 13.25 18.3 13.4208 19.05 13.6625C19.8 13.9042 20.5 14.2 21.15 14.55C21.75 14.8833 22.2083 15.2542 22.525 15.6625C22.8417 16.0708 23 16.5167 23 17V20H19ZM9 12C7.9 12 6.95833 11.6083 6.175 10.825C5.39167 10.0417 5 9.1 5 8C5 6.9 5.39167 5.95833 6.175 5.175C6.95833 4.39167 7.9 4 9 4C10.1 4 11.0417 4.39167 11.825 5.175C12.6083 5.95833 13 6.9 13 8C13 9.1 12.6083 10.0417 11.825 10.825C11.0417 11.6083 10.1 12 9 12ZM19 8C19 9.1 18.6083 10.0417 17.825 10.825C17.0417 11.6083 16.1 12 15 12C14.8167 12 14.5833 11.9792 14.3 11.9375C14.0167 11.8958 13.7833 11.85 13.6 11.8C14.05 11.2667 14.3958 10.675 14.6375 10.025C14.8792 9.375 15 8.7 15 8C15 7.3 14.8792 6.625 14.6375 5.975C14.3958 5.325 14.05 4.73333 13.6 4.2C13.8333 4.11667 14.0667 4.0625 14.3 4.0375C14.5333 4.0125 14.7667 4 15 4C16.1 4 17.0417 4.39167 17.825 5.175C18.6083 5.95833 19 6.9 19 8Z"
                                fill="#808080"
                                />
                            </g>
                            </svg>
                        </span>
                        部署
                        </p>
                        <p className="text-base alu-text-base-2">{jobDetail.department || 'ー'}</p>
                    </div>
                    <div className="flex items-center gap-[1.125rem]">
                        <p className="min-w-[80px] text-base alu-text-base-2 flex items-center">
                        <span className="mr-1">
                            <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            >
                            <mask
                                id="mask0_13672_12413"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="24"
                                height="24"
                            >
                                <rect width="24" height="24" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_13672_12413)">
                                <path
                                d="M4 21C3.45 21 2.97917 20.8042 2.5875 20.4125C2.19583 20.0208 2 19.55 2 19V8C2 7.45 2.19583 6.97917 2.5875 6.5875C2.97917 6.19583 3.45 6 4 6H8V4C8 3.45 8.19583 2.97917 8.5875 2.5875C8.97917 2.19583 9.45 2 10 2H14C14.55 2 15.0208 2.19583 15.4125 2.5875C15.8042 2.97917 16 3.45 16 4V6H20C20.55 6 21.0208 6.19583 21.4125 6.5875C21.8042 6.97917 22 7.45 22 8V19C22 19.55 21.8042 20.0208 21.4125 20.4125C21.0208 20.8042 20.55 21 20 21H4ZM10 6H14V4H10V6Z"
                                fill="#808080"
                                />
                            </g>
                            </svg>
                        </span>
                        職種
                        </p>
                        <p className="text-base alu-text-base-2">{jobDetail.occupation}</p>
                    </div>
                    <div className="flex items-center gap-[1.125rem]">
                        <p className="min-w-[80px] text-base alu-text-base-2 flex items-center">
                        <span className="mr-1">
                            <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            >
                            <mask
                                id="mask0_11508_17889"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="24"
                                height="24"
                            >
                                <rect width="24" height="24" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_11508_17889)">
                                <path
                                d="M3 21V7H7V3H17V11H21V21H13V17H11V21H3ZM5 19H7V17H5V19ZM5 15H7V13H5V15ZM5 11H7V9H5V11ZM9 15H11V13H9V15ZM9 11H11V9H9V11ZM9 7H11V5H9V7ZM13 15H15V13H13V15ZM13 11H15V9H13V11ZM13 7H15V5H13V7ZM17 19H19V17H17V19ZM17 15H19V13H17V15Z"
                                fill="#808080"
                                />
                            </g>
                            </svg>
                        </span>
                        勤務地
                        </p>
                        <p className="text-base alu-text-base-2">
                            {jobDetail.location}
                        </p>
                    </div>
                    <div className="flex items-center gap-[1.125rem]">
                        <p className="min-w-[80px] text-base alu-text-base-2 flex items-center">
                        <span className="mr-1">
                            <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            >
                            <mask
                                id="mask0_11508_17894"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="24"
                                height="24"
                            >
                                <rect width="24" height="24" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_11508_17894)">
                                <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM11.2857 16V18.8571H12.7143V16H16.2857V14.5714H12.7143V13.1429H16.2857V11.7143H13.375L17 6H15.3036L12 11.2143L8.69643 6H7L10.625 11.7143H7.71429V13.1429H11.2857V14.5714H7.71429V16H11.2857Z"
                                fill="#808080"
                                />
                            </g>
                            </svg>
                        </span>
                        給与
                        </p>
                        <p className="text-base alu-text-base-2">
                        {jobDetail.salary || 'ー'}
                        </p>
                    </div>
                    </div>
                    <p className="text-sm alu-text-base-1 font-normal">
                        {jobDetail.jobDate}
                    </p>
                </div>

                <div className="px-6 mb-8">
                    <div className="w-full h-[1px] bg-[#E0E0E0]"></div>
                </div>

                <p className="px-6 text-sm font-normal alu-text-base-2 whitespace-pre-wrap break-all mb-8">
                    {renderContentWithTrackedLinks(jobDetail.message_from_department)}
                </p>

                <div className="px-6 mb-8">
                    <CompanyInformationTab jobInfomation={jobDetail} />
                </div>

                <div className="px-6">
                    <JobInformationTab jobInfomation={jobDetail} />
                </div>
                </div>

                <div className="shrink-0 w-[320px]">
                    <CustomButton
                        onClick={handleSetIsPublic}
                        type="button"
                        customStyle="mb-4"
                        width="full"
                        size="md"
                        disabled={!staffPermission[PERMISSIONS.JOB_EDIT] || (!jobDetail.is_draft && !jobDetailDataTemp) || isPublishedItem}
                    >
                        {jobDetail.is_public_scheduled ? "公開予約" : "公開"}
                    </CustomButton>
                    <CustomButton
                        onClick={handleGoToEdit}
                        type="button"
                        width="full"
                        variant="outline"
                        size="md"
                        disabled={!staffPermission[PERMISSIONS.JOB_EDIT]}
                    >
                        編集画面に戻る
                    </CustomButton>{" "}
                </div>
            </div>
            </div>
        </div>
        <Copyright view="company" />
        {showLeaveConfirm && !isNavigatingAway && <UnsaveConfirmPopup
            open={showLeaveConfirm}
            onClose={() => dispatch(setShowLeaveConfirm(false))}
            confirmContent={"編集中の内容を保存せずに終了しますか？"}
            onOtherAction={() => {dispatch(setShowLeaveConfirm(false));dispatch(setIsFormDirty(false));dispatch(setJobDetailDataTemp(null));dispatch(setJobDetailData([]));navigate(-1);}}
        />}
        </>
    );
};

export default JobPreview;
